'use no memo';

import {differenceInDays} from 'date-fns';
import {match} from 'ts-pattern';

import {always, isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

import {GetInvoiceV4ApiResponse, InvoiceTypes} from '@dms/api/core';
import {
  useGetBalanceInvoiceV4Query,
  useGetCorrectiveTaxDocumentV5Query,
  useGetInvoiceProformaV4Query,
  useGetInvoiceV4Query,
  useGetTaxDocumentForPaymentV4Query,
} from '@dms/api/finance';
import i18n from '@dms/i18n';

import {getApiDateString, LiteralUnionAutocomplete, Nullish, parseDate} from 'shared/web';

import {useGetInitialDocumentSeriesId} from './useGetInitialDocumentSeriesId';

export const useGetDocumentData = (invoiceType: string | Nullish, id: string | Nullish) => {
  const isParametersError = isNil(id) || isNil(invoiceType);
  const getSkipArg = (type: LiteralUnionAutocomplete<InvoiceTypes>[]) =>
    isParametersError || (!type?.includes(invoiceType) && isNotNil(type));

  const {
    data: invoiceData,
    isLoading: isLoadingInvoice,
    isError: isInvoiceError,
  } = useGetInvoiceV4Query({invoiceId: id!}, {skip: getSkipArg(['ISSUED'])});
  const {
    data: balanceInvoiceData,
    isLoading: isLoadingBalanceInvoice,
    isError: isBalanceInvoiceError,
  } = useGetBalanceInvoiceV4Query({balanceInvoiceId: id!}, {skip: getSkipArg(['BALANCE_INVOICE'])});
  const {
    data: proformaInvoiceData,
    isLoading: isLoadingProformaInvoice,
    isError: isProformaInvoiceError,
  } = useGetInvoiceProformaV4Query({invoiceProformaId: id!}, {skip: getSkipArg(['PROFORMA'])});

  const {
    data: correctiveTaxDocument,
    isLoading: isLoadingCorrectiveTaxDocument,
    isError: isCorrectiveTaxDocumentError,
  } = useGetCorrectiveTaxDocumentV5Query(
    {correctiveTaxDocumentId: id!},
    {skip: getSkipArg(['CORRECTIVE_TAX_DOCUMENT'])}
  );

  const {
    data: taxDocumentForPayment,
    isLoading: isLoadingTaxDocumentForPayment,
    isError: isTaxDocumentForPaymentError,
  } = useGetTaxDocumentForPaymentV4Query(
    {id: id!},
    {skip: getSkipArg(['TAX_DOCUMENT_FOR_PAYMENT'])}
  );

  const {documentSeriesId, isLoadingDocumentSeries, isDocumentSeriesError} =
    useGetInitialDocumentSeriesId('corrective_tax_document');

  const isFetchingError =
    isBalanceInvoiceError ||
    isInvoiceError ||
    isProformaInvoiceError ||
    isDocumentSeriesError ||
    isCorrectiveTaxDocumentError ||
    isTaxDocumentForPaymentError;

  const isError = isParametersError || isFetchingError;
  const isLoading =
    isLoadingInvoice ||
    isLoadingBalanceInvoice ||
    isLoadingProformaInvoice ||
    isLoadingDocumentSeries ||
    isLoadingCorrectiveTaxDocument ||
    isLoadingTaxDocumentForPayment;

  const dataByType = match(invoiceType)
    .with('ISSUED', always(invoiceData))
    .with('BALANCE_INVOICE', always(balanceInvoiceData?.invoice))
    .with('PROFORMA', always(proformaInvoiceData))
    .with('CORRECTIVE_TAX_DOCUMENT', always(correctiveTaxDocument))
    .with('TAX_DOCUMENT_FOR_PAYMENT', always(taxDocumentForPayment))
    .otherwise(always(null));

  const data = isNil(dataByType)
    ? null
    : {
        ...dataByType,
        customFields: (dataByType as GetInvoiceV4ApiResponse)?.customFields ?? [],
        cancellable: (dataByType as GetInvoiceV4ApiResponse)?.cancellable ?? false,
        paymentState: (dataByType as GetInvoiceV4ApiResponse)?.paymentState ?? null,
        invoiceCategory: (dataByType as GetInvoiceV4ApiResponse)?.invoiceCategory,
        dueSinceIssueDateInDays: isNotNil(dataByType)
          ? differenceInDays(parseDate(dataByType.dueDate), parseDate(dataByType.issueDate))
          : undefined,
        paymentInfo: {
          bankName: dataByType.paymentInfo.bankName ?? undefined,
          paymentMethod: dataByType.paymentInfo.paymentMethod ?? 'BANK_TRANSFER',
          bankAccount: dataByType.paymentInfo.bankAccount ?? undefined,
          iban: dataByType.paymentInfo.iban ?? undefined,
          swift: dataByType.paymentInfo.swift ?? undefined,
          constantSymbol: dataByType.paymentInfo.constantSymbol ?? undefined,
          specificSymbol: dataByType.paymentInfo.specificSymbol ?? undefined,
          variableSymbol: dataByType.paymentInfo.variableSymbol ?? undefined,
        },
        dateOfReception: getApiDateString(new Date()),
        footerNote: (dataByType as GetInvoiceV4ApiResponse)?.footerNote ?? '',
        documentSeriesId,
        isExchangeRateRatioEnabled: isNotNil(dataByType?.exchangeRateRatio),
        exchangeRateRatio: {
          amount: dataByType?.exchangeRateRatio?.amount.toString() ?? '1',
          currency: dataByType?.exchangeRateRatio?.currency ?? 'EUR',
          ratio: dataByType?.exchangeRateRatio?.ratio ?? null,
          type: i18n.t('general.actions.recalculate'),
        },
        additionalCustomer: hadAdditionalCustomer(dataByType)
          ? {
              customer: {
                ...dataByType.additionalCustomer,
                id: dataByType.additionalCustomer?.customerId,
              },
              type: dataByType.additionalCustomer?.type,
              contractInformationId: dataByType.additionalCustomer?.contractInformationId,
            }
          : null,
      };

  return {data, isError, isLoading} as const;
};

type ItemWithAdditionalCustomer<Item> = Item & {
  additionalCustomer: GetInvoiceV4ApiResponse['additionalCustomer'];
};
const hadAdditionalCustomer = <T extends Record<string, unknown>>(
  val: T
): val is ItemWithAdditionalCustomer<T> =>
  isNotNil((val as ItemWithAdditionalCustomer<T>)?.additionalCustomer);
